import FilterButton from '@/components/page-specific/catering/supplier-menu/components/filter-button'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import type { Meal } from '@/generated/gql/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useQuery } from '@apollo/client'
import Close from '@mui/icons-material/Close'
import HourglassBottom from '@mui/icons-material/HourglassBottom'
import HourglassTop from '@mui/icons-material/HourglassTop'
import Tune from '@mui/icons-material/Tune'
import {
  Box,
  Button,
  Container,
  Dialog,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInterval } from 'react-use'

import FiltersPanelMui from '../FiltersPanel/FiltersPanelMui'
import FiltersPanelMuiV2 from '../FiltersPanelV2/FiltersPanelMuiV2'
import { MobileFiltersList } from '../FiltersPanelV2/MobileFiltersList'
import type { BudgetFilter, BudgetFilterValue, FilterCategory } from '../FiltersPanelV2/types'
import type { CanteenCartItemInput } from '../MenuCart/types'
import type { CanteenCartItem } from '../MenuCartV2/types'
import MenuSort, { MenuSortOption } from '../MenuSort'
import OrderAgainGraphql, { OrderAgainMenuItemLookup } from '../OrderAgainGraphql'
import OrderedItemsGraphql from '../OrderedItemsGraphql'
import { CANTEEN_NAVBAR_ID } from '../shared/constants'
import { serializeMenuQueryDate } from '../state/menuQueryDates'
import { getTimeslotOrderWindow } from './menuBodyDates'
import MenuItems, { canteenMenuItemsMenuItemDataFragment } from './MenuItems'
import OrderingOpensCountdown from './OrderingOpensCountdown'
import { useMenuItemActions } from './useMenuItemActions'

const canteenMenuBodyCurrentMenuItemsQuery = graphql(`
  query CanteenMenuBody_CurrentMenuItemsQuery(
    $meal: Meal!
    $date: DateTime!
    $timeslotStart: DateTime!
    $mainsFilters: CCMenuItemFilters!
    $sidesFilters: CCMenuItemFilters!
    $drinksFilters: CCMenuItemFilters!
    $vendorFilters: CCVendorMenusQueryInput
  ) {
    me {
      id
      ccMenu(input: { meal: $meal, date: $date, timeslotStart: $timeslotStart }) {
        id
        mealServiceData {
          id
          ...CanteenOrderedItems_MenuDataFragment
          ...CanteenOrderAgain_MenuDataFragment
          timeslots {
            id
            ordersOpenTime
            isPreOrdersOpenTime
            ordersAreOpen
            ordersCloseTime
            isPostOrdersCloseTime
            id
            allVendorMenus: vendorMenus {
              id
              ...CanteenMenuFiltersPanel_VendorMenuDataFragment
              ...CanteenMenuFiltersPanel_VendorMenuDataFragmentV2
            }
            filteredVendorMenus: vendorMenus(input: $vendorFilters) {
              id
              vendor {
                id
                ...CanteenMenuItems_VendorDataFragment
              }
              mains: menuItems(limit: 200, filters: $mainsFilters) {
                cacheKeys
                pageInfo {
                  count
                }
                items {
                  id
                  liveMenuItem {
                    name
                  }
                  liveVariation {
                    priceCustomerFacing
                  }
                  minimumPurchasablePrice
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
              sides: menuItems(limit: 200, filters: $sidesFilters) {
                cacheKeys
                pageInfo {
                  count
                }
                items {
                  id
                  liveMenuItem {
                    name
                  }
                  liveVariation {
                    priceCustomerFacing
                  }
                  minimumPurchasablePrice
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
              drinks: menuItems(limit: 200, filters: $drinksFilters) {
                cacheKeys
                pageInfo {
                  count
                }
                items {
                  id
                  liveMenuItem {
                    name
                  }
                  liveVariation {
                    priceCustomerFacing
                  }
                  minimumPurchasablePrice
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
            }
          }
        }
      }
    }
  }
`)

interface MenuBodyProps {
  meal: Meal
  date: string
  timeslot: string
  timezone: string | null | undefined
  cartItems: Array<CanteenCartItem>
  menuFilters: { dietaries: string[]; allergens: string[]; vendors: string[] }
  ordersAttempted: number
  hidePrices: boolean
  onClearFilters: () => void
  onToggleFilterValue: (category: FilterCategory, value: string) => void
  addToCart: (item: CanteenCartItemInput) => void
  onSuccessfulCancellation: () => Promise<void>
  useNewCcUi?: boolean
  filteredMenuFilterCount?: Record<string, any>
  searchTerm?: string
  onSearchTermChange?: (value: string) => void
  budgetFilter: BudgetFilter
  onToggleBudgetFilter: (value: BudgetFilterValue) => void
  balance: number
  availableSubsidy: number
  unlimitedSubsidy: boolean
  shouldShowFilters?: boolean
}

export function MenuBody({
  timeslot,
  timezone,
  date,
  meal,
  cartItems = [],
  menuFilters,
  ordersAttempted,
  hidePrices,
  onSuccessfulCancellation,
  onToggleFilterValue,
  onClearFilters,
  addToCart,
  useNewCcUi = false,
  filteredMenuFilterCount = {},
  searchTerm = '',
  onSearchTermChange = () => {},
  budgetFilter,
  onToggleBudgetFilter,
  balance,
  availableSubsidy,
  unlimitedSubsidy,
  shouldShowFilters = true,
}: MenuBodyProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const useServerSideMenuClosedLogic = useFeatureFlag(
    SPLITS.USE_SERVER_SIDE_CANTEEN_MENU_CLOSED_LOGIC,
  )
  const useCcReorderSection = useFeatureFlag(SPLITS.USE_CC_REORDER_SECTION)
  const showSortForCcMenu = useFeatureFlag(SPLITS.SHOW_SORT_FOR_CC_MENU)
  const [sortOption, setSortOption] = useState<MenuSortOption>('itemType')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dateISOString = serializeMenuQueryDate(date, timezone)
  const timeslotISOString = serializeMenuQueryDate(timeslot, timezone)
  const dietaries = menuFilters.dietaries
  const allergens = menuFilters.allergens
  const vendors = menuFilters.vendors

  const {
    data: menuData,
    loading,
    refetch,
    previousData: previousMenuData,
  } = useQuery(
    canteenMenuBodyCurrentMenuItemsQuery,
    dateISOString !== null && timeslotISOString !== null
      ? {
          variables: {
            meal,
            date: dateISOString,
            timeslotStart: timeslotISOString,
            mainsFilters: {
              menuItemType: ['main'],
              dietaries,
              allergens,
            },
            sidesFilters: {
              menuItemType: ['side'],
              dietaries,
              allergens,
            },
            drinksFilters: {
              menuItemType: ['drink'],
              dietaries,
              allergens,
            },
            vendorFilters: {
              ...(vendors.length && { includeVendorsById: vendors }),
            },
          },
          skip: false,
          fetchPolicy: 'cache-and-network',
        }
      : { skip: true },
  )

  const { isAddItemDisabled, handleSeeMore, handleAddToCart, menuItemDetailModal } =
    useMenuItemActions({
      cartItems,
      addToCart,
      hidePrices,
      useNewCcUi,
    })

  // Keyed by itemReference (== liveVariation._id), built from the same
  // already-loaded menu items as the main grid — not the further
  // search-term/subsidy-filtered menuItemData below — so the order-again
  // strip's add-to-cart isn't affected by the user's current search text.
  const menuItemLookup = useMemo(() => {
    const lookup: OrderAgainMenuItemLookup = new Map()
    const vendorMenus =
      menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.filteredVendorMenus ||
      previousMenuData?.me.ccMenu?.mealServiceData.timeslots[0]?.filteredVendorMenus ||
      []
    for (const menu of vendorMenus) {
      const menuItems = [...menu.mains.items, ...menu.sides.items, ...menu.drinks.items]
      for (const item of menuItems) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const data = useFragment(canteenMenuItemsMenuItemDataFragment, item)
        if (data.liveVariation._id) {
          lookup.set(data.liveVariation._id, { item, vendor: menu.vendor })
        }
      }
    }
    return lookup
  }, [menuData, previousMenuData])

  const useOldDesktop = !useNewCcUi && !isMobile
  const useOldMobile = !useNewCcUi && isMobile
  const useNewMobile = useNewCcUi && isMobile

  const [isSticky, setIsSticky] = useState(false)
  const [navHeight, setNavHeight] = useState(70)
  const observableButtonRef = useRef<HTMLDivElement | null>(null)
  const filterBarRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!useNewMobile) return

    const navEl = document.getElementById(CANTEEN_NAVBAR_ID)
    if (!navEl) return

    const updateNavHeight = () => setNavHeight(navEl.getBoundingClientRect().height)
    updateNavHeight()

    const resizeObserver = new ResizeObserver(updateNavHeight)
    resizeObserver.observe(navEl)

    return () => resizeObserver.disconnect()
  }, [useNewMobile])

  useEffect(() => {
    if (!useNewMobile) {
      setIsSticky(false)
      return
    }

    if (!observableButtonRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting)
      },
      { root: null, threshold: 0, rootMargin: `-${navHeight}px 0px 0px 0px` },
    )

    observer.observe(observableButtonRef.current)

    return () => observer.disconnect()
  }, [useNewMobile, navHeight])

  const prevOrdersAttemptedRef = useRef(ordersAttempted)
  useEffect(() => {
    if (ordersAttempted > prevOrdersAttemptedRef.current) {
      refetch()
    }
    prevOrdersAttemptedRef.current = ordersAttempted
  }, [ordersAttempted, refetch])

  useInterval(() => {
    if (!menuData?.me.ccMenu) return
    const timeslotOrdersOpenTime =
      menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersOpenTime || ''
    const timeslotOrdersCloseTime =
      menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersCloseTime || ''
    const { isWithinOrderWindow: menuShouldBeOpen } = getTimeslotOrderWindow(
      timeslotOrdersOpenTime,
      timeslotOrdersCloseTime,
    )
    const menuIsOpenValue = Boolean(menuData.me.ccMenu.mealServiceData.timeslots[0]?.ordersAreOpen)
    if (menuShouldBeOpen !== menuIsOpenValue) {
      refetch()
    }
  }, 1000)

  const timeslotOrdersOpenTime =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersOpenTime || ''
  const timeslotOrdersCloseTime =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersCloseTime || ''

  const orderWindow = getTimeslotOrderWindow(timeslotOrdersOpenTime, timeslotOrdersCloseTime)

  const menuNotOpenForOrdersYet = useServerSideMenuClosedLogic
    ? Boolean(menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.isPreOrdersOpenTime)
    : orderWindow.isBeforeOpening
  const menuCutOffPassed = useServerSideMenuClosedLogic
    ? Boolean(menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.isPostOrdersCloseTime)
    : orderWindow.hasCutoffPassed
  const timeslotOrdersOpen = useServerSideMenuClosedLogic
    ? Boolean(menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersAreOpen)
    : !menuNotOpenForOrdersYet && !menuCutOffPassed

  const isDeliveryFeeItem = (item: { liveMenuItem: { name?: string | null } }) =>
    /delivery/i.test(item.liveMenuItem.name ?? '')

  const menuItemData =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.filteredVendorMenus.map((menu) => {
      const res = {
        vendor: menu.vendor,
        mains: menu.mains.items.filter((item) => !isDeliveryFeeItem(item)),
        sides: menu.sides.items.filter((item) => !isDeliveryFeeItem(item)),
        drinks: menu.drinks.items.filter((item) => !isDeliveryFeeItem(item)),
      }

      if (searchTerm) {
        // cleaning searchTerm here to keep display value matching exact user input
        const cleanSearchTerm = searchTerm.toLowerCase().trim()
        res.mains = res.mains.filter((item) =>
          item.liveMenuItem.name?.toLowerCase().includes(cleanSearchTerm),
        )
        res.sides = res.sides.filter((item) =>
          item.liveMenuItem.name?.toLowerCase().includes(cleanSearchTerm),
        )
        res.drinks = res.drinks.filter((item) =>
          item.liveMenuItem.name?.toLowerCase().includes(cleanSearchTerm),
        )
      }

      if (!unlimitedSubsidy && budgetFilter) {
        const allowance =
          budgetFilter === 'subsidy'
            ? Math.round((availableSubsidy ?? 0) * 100)
            : Math.round((balance ?? 0) * 100) + Math.round((availableSubsidy ?? 0) * 100)
        res.mains = res.mains.filter((item) => item.minimumPurchasablePrice <= allowance)
        res.sides = res.sides.filter((item) => item.minimumPurchasablePrice <= allowance)
        res.drinks = res.drinks.filter((item) => item.minimumPurchasablePrice <= allowance)
      }

      return res
    }) || []

  const totalItemCount = menuItemData.reduce(
    (total, menu) => total + menu.mains.length + menu.sides.length + menu.drinks.length,
    0,
  )

  const handleSuccessfulCancellation = async () => {
    refetch()
    await onSuccessfulCancellation()
  }

  const vendorMenus =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus ||
    previousMenuData?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus

  // Identity of the menu being viewed — distinct from the dietary/allergen/vendor
  // filters, which change query variables without changing which menu this is.
  const orderAgainMenuIdentity = `${meal}|${dateISOString}|${timeslotISOString}`
  const freshOrderAgainMealServiceData = menuData?.me.ccMenu?.mealServiceData
  const lastGoodOrderAgainRef = useRef<{
    identity: string
    data: NonNullable<typeof freshOrderAgainMealServiceData>
  } | null>(null)

  useEffect(() => {
    if (freshOrderAgainMealServiceData) {
      lastGoodOrderAgainRef.current = {
        identity: orderAgainMenuIdentity,
        data: freshOrderAgainMealServiceData,
      }
    }
  }, [freshOrderAgainMealServiceData, orderAgainMenuIdentity])

  // Falls back to the last good data only when the menu identity (meal/date/timeslot)
  // is unchanged — e.g. toggling a dietary/allergen/vendor filter, which changes query
  // variables and briefly clears `data`; without this fallback that would unmount
  // OrderAgainGraphql and reset OrderAgainSection's expanded/collapsed state. Switching
  // timeslot/date/meal is a genuinely different menu, so we deliberately don't paper
  // over that with stale data — doing so could let add-to-cart submit an item against
  // the wrong timeslot.
  const orderAgainMealServiceData =
    freshOrderAgainMealServiceData ??
    (lastGoodOrderAgainRef.current?.identity === orderAgainMenuIdentity
      ? lastGoodOrderAgainRef.current.data
      : undefined)

  return (
    <>
      {menuItemDetailModal}
      {useOldMobile ? (
        <Stack direction="row-reverse" paddingX={3}>
          <Button
            startIcon={<Tune fontSize="small" />}
            sx={{ width: 'fit-content' }}
            size="small"
            onClick={() => setFiltersOpen(true)}>
            Filters
          </Button>
        </Stack>
      ) : null}

      {useNewMobile ? (
        <Stack paddingY={1} spacing={1}>
          {/* Box used to observe scroll position for when filter button should become sticky (i.e. fixed) */}
          <Box ref={observableButtonRef} />
          <Box
            ref={filterBarRef}
            sx={{
              position: isSticky ? 'fixed' : 'relative',
              top: isSticky ? `${navHeight}px` : 'auto',
              zIndex: (theme) => theme.zIndex.appBar - 1,
              backgroundColor: 'transparent',
            }}>
            <FilterButton
              onClick={() => setFiltersOpen(true)}
              startIcon={<Tune />}
              variant="white"
              sx={{ width: 'fit-content' }}>
              Menu & Filters
            </FilterButton>
          </Box>
          {isSticky && <Box sx={{ height: filterBarRef.current?.offsetHeight || 0 }} />}
          <MobileFiltersList
            menuFilters={menuFilters}
            onToggleFilterValue={onToggleFilterValue}
            menuData={vendorMenus}
            searchTerm={searchTerm}
            onClearSearchTerm={() => onSearchTermChange('')}
          />
          {menuFilters.dietaries.length > 0 ||
          menuFilters.allergens.length > 0 ||
          menuFilters.vendors.length > 0 ? (
            <Button
              variant="text"
              sx={{ width: 'fit-content', paddingY: 0 }}
              size="small"
              onClick={onClearFilters}>
              {t('common:clearAll')}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      <Stack
        spacing={3}
        paddingX={useNewCcUi ? 0 : 2}
        paddingY={useNewCcUi ? 0 : 2}
        paddingTop={isMobile ? 0 : 2}
        direction={{ md: 'row', xs: 'column' }}>
        {useOldMobile ? (
          <Dialog onClose={() => setFiltersOpen(false)} fullScreen open={filtersOpen}>
            <IconButton
              sx={{
                bgcolor: 'inverted.main',
                marginLeft: 'auto',
                maxWidth: 'fit-content',
                position: 'sticky',
                top: 0,
                zIndex: 3,
              }}
              onClick={() => setFiltersOpen(false)}>
              <Close />
            </IconButton>
            <Container>
              <FiltersPanelMui
                onClearFilters={onClearFilters}
                menuFilters={menuFilters}
                onToggleFilterValue={onToggleFilterValue}
                loading={loading}
                menuData={vendorMenus}
              />
            </Container>
          </Dialog>
        ) : null}

        {useOldDesktop ? (
          <Box sx={{ minWidth: '260px', width: { xs: '100%', md: '260px' } }}>
            <FiltersPanelMui
              onClearFilters={onClearFilters}
              onToggleFilterValue={onToggleFilterValue}
              menuFilters={menuFilters}
              loading={loading}
              menuData={vendorMenus}
            />
          </Box>
        ) : null}

        {useNewMobile ? (
          <Drawer
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: '80vw',
              },
            }}>
            <Box>
              <FiltersPanelMuiV2
                shouldShowFilters={shouldShowFilters}
                filteredMenuFilterCount={filteredMenuFilterCount}
                onClearFilters={onClearFilters}
                menuFilters={menuFilters}
                onToggleFilterValue={onToggleFilterValue}
                loading={loading}
                menuData={vendorMenus}
                searchTerm={searchTerm}
                onSearchChange={onSearchTermChange}
                onHideFiltersDrawer={() => setFiltersOpen(false)}
                budgetFilter={budgetFilter}
                onToggleBudgetFilter={onToggleBudgetFilter}
                hideCCPrices={hidePrices}
              />
            </Box>
          </Drawer>
        ) : null}

        <Stack width="100%" gap={1}>
          {menuData?.me.ccMenu?.mealServiceData ? (
            <OrderedItemsGraphql
              onSuccessfulCancellation={handleSuccessfulCancellation}
              menuData={menuData.me.ccMenu.mealServiceData}
              hidePrices={hidePrices}
              useNewCcUi={useNewCcUi}
            />
          ) : null}
          {loading || timeslotOrdersOpen ? (
            <>
              {useCcReorderSection && orderAgainMealServiceData ? (
                <OrderAgainGraphql
                  menuData={orderAgainMealServiceData}
                  hidePrices={hidePrices}
                  searchTerm={searchTerm}
                  menuItemLookup={menuItemLookup}
                  isAddItemDisabled={isAddItemDisabled}
                  onAddToCart={handleAddToCart}
                  onSeeMore={handleSeeMore}
                />
              ) : null}
              {showSortForCcMenu ? (
                <MenuSort
                  itemCount={totalItemCount}
                  sortOption={sortOption}
                  onChangeSortOption={setSortOption}
                />
              ) : null}
              <MenuItems
                vendorMenus={menuItemData}
                sortOption={sortOption}
                loading={loading}
                cartItems={cartItems}
                hidePrices={hidePrices}
                useNewCcUi={useNewCcUi}
                budgetFilter={budgetFilter}
                balance={balance}
                availableSubsidy={availableSubsidy}
                unlimitedSubsidy={unlimitedSubsidy}
                isAddItemDisabled={isAddItemDisabled}
                handleSeeMore={handleSeeMore}
                handleAddToCart={handleAddToCart}
              />
            </>
          ) : menuNotOpenForOrdersYet && timeslotOrdersOpenTime ? (
            <OrderingOpensCountdown ordersOpenTime={timeslotOrdersOpenTime} />
          ) : (
            <Stack spacing={1} width="100%" alignItems="center">
              {menuNotOpenForOrdersYet ? (
                <HourglassTop sx={{ fontSize: '4rem' }} color="info" fontSize="large" />
              ) : null}
              {menuCutOffPassed ? (
                <HourglassBottom sx={{ fontSize: '4rem' }} color="info" fontSize="large" />
              ) : null}
              <Typography variant="subtitle2">
                This timeslot is {menuNotOpenForOrdersYet ? 'not open yet' : null}
                {menuCutOffPassed ? 'now closed' : null}
              </Typography>
              <Typography>
                {menuNotOpenForOrdersYet ? <>Check again after orders open</> : null}
                {menuCutOffPassed ? <>Try another date or timeslot</> : null}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </>
  )
}
